import { getActiveAuthSession } from '@modeling/account';
import { createClient } from '@modeling/git-file-viewer';
import { SCHEMA_PROFILES_DIR } from '../config/schemaGit';
import type { SchemaProfile } from '../types/schemaProfile';
import { isGitCredentialNotConfiguredError } from '../utils/gitErrors';
import { resolveGitApiBaseUrl } from './env';
import { pydcApi } from './pydcApi';
import type { SchemaGitSettings } from '../storage/schemaGitSettings';

function getToken(): string {
  return getActiveAuthSession()?.token ?? '';
}

export type SchemaGitConnection = SchemaGitSettings;

export interface SchemaReloadResult {
  dryRun: boolean;
  schemaProfile: string;
  repoUrl: string;
  ref: string;
  keys: string[];
  count: number;
  deleted?: number;
  loaded?: number;
}

export interface SchemaReloadParams {
  repoUrl: string;
  schemaProfile: string;
  branch: string;
  dryRun: boolean;
}

export type ListSchemaProfilesResult =
  | { ok: true; profiles: SchemaProfile[] }
  | { ok: false; reason: 'credential_required' };

/** 从 Git 仓库 schema/profiles/ 目录发现 profile 列表（凭据问题不抛错） */
export async function listSchemaProfilesFromGit(
  conn: SchemaGitConnection,
): Promise<ListSchemaProfilesResult> {
  const baseURL = resolveGitApiBaseUrl();
  const { repoUrl, branch } = conn;

  if (!baseURL) {
    throw new Error('未配置 REACT_APP_GIT_API_BASE_URL');
  }
  if (!repoUrl) {
    throw new Error('请填写 Git 仓库 URL');
  }

  try {
    const client = createClient(baseURL, getToken);
    const tree = await client.getTree(repoUrl, branch, SCHEMA_PROFILES_DIR);

    const profiles = tree.items
      .filter((item) => item.type === 'directory')
      .map((item) => ({
        id: item.name,
        name: item.name,
        createdAt: '',
        updatedAt: '',
      }))
      .sort((a, b) => a.id.localeCompare(b.id));

    return { ok: true, profiles };
  } catch (err) {
    if (isGitCredentialNotConfiguredError(err)) {
      return { ok: false, reason: 'credential_required' };
    }
    throw err;
  }
}

export async function getSchemaProfileFromGit(
  id: string,
  conn: SchemaGitConnection,
): Promise<SchemaProfile | undefined> {
  const result = await listSchemaProfilesFromGit(conn);
  if (!result.ok) return undefined;
  return result.profiles.find((p) => p.id === id);
}

/** 调用 pydc 物化 tenant scope schema_profile */
export async function reloadSchemaProfile(params: SchemaReloadParams): Promise<SchemaReloadResult> {
  const response = await pydcApi.post<SchemaReloadResult>('/admin/schema/profiles/reload', {
    repoUrl: params.repoUrl,
    schemaProfile: params.schemaProfile,
    branch: params.branch,
    dryRun: params.dryRun,
  });
  return response.data;
}
